import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTable,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";
import CustomDatePicker from "../../components/CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineUsageEvaluateReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    StartDate: "",
    EndDate: "",
    EvaluateName: "",
    Province: "",
    VaccineID: "",
    LivestockTypeID: "",
    Status: "",
  });
  const today = new Date();
  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;
  const [evaluateData, setEvaluateData] = useState([]);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [livestockTypeOptions, setLivestockTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ดึงข้อมูล Province List
  const fetchProvinceOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getProvinceList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setProvinceOptions(response.ProvinceList || []);
      } else {
        console.error("Failed to fetch provinces:", response.error);
      }
    } catch (error) {
      console.error("Error fetching provinces:", error);
    }
  };

  // ดึงข้อมูล LivestockType List
  const fetchLivestockTypeOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setLivestockTypeOptions(response.LivestockTypeList || []);
      } else {
        console.error("Failed to fetch livestock types:", response.error);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    }
  };

  // เรียก API getVaccineUsageEvaluateSummary
  const fetchEvaluateReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineUsageEvaluateSummary",
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          EvaluateName: filterData.EvaluateName || "",
          Province: filterData.Province || "",
          VaccineID: filterData.VaccineID || "",
          LivestockTypeID: filterData.LivestockTypeID || "",
          Status:
            filterData.Status === "" ? undefined : parseInt(filterData.Status),
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setEvaluateData(response.summary || []);
      } else {
        setEvaluateData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching evaluate report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchProvinceOptions();
      fetchLivestockTypeOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รหัส</span>
        </HStack>
      ),
      selector: (row) => row.ID,
      sortable: true,
      width: "80px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่ประเมิน</span>
        </HStack>
      ),
      selector: (row) =>
        new Date(row.EvaluateDate).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อผู้ประเมิน</span>
        </HStack>
      ),
      selector: (row) => row.EvaluateName,
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>บริษัท</span>
        </HStack>
      ),
      selector: (row) => row.EvaluateCompany,
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จังหวัด</span>
        </HStack>
      ),
      selector: (row) => row.Province,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รหัสล็อต</span>
        </HStack>
      ),
      selector: (row) => row.LotID || "-",
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่รับ</span>
        </HStack>
      ),
      selector: (row) =>
        new Date(row.ReceiveDate).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่ใช้</span>
        </HStack>
      ),
      selector: (row) =>
        new Date(row.UsedDate).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ประเภทปศุสัตว์</span>
        </HStack>
      ),
      selector: (row) => row.LivestockTypeID,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนปศุสัตว์ (ตัว)</span>
        </HStack>
      ),
      selector: (row) => row.LivestockAmount || 0,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนแพ้วัคซีน (ตัว)</span>
        </HStack>
      ),
      selector: (row) => row.AllergicAmount || 0,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รายละเอียดการแพ้</span>
        </HStack>
      ),
      selector: (row) => row.AllergicDetail || "-",
      sortable: true,
      wrap: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนผลข้างเคียง (ตัว)</span>
        </HStack>
      ),
      selector: (row) => row.SideEffectAmount || 0,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รายละเอียดผลข้างเคียง</span>
        </HStack>
      ),
      selector: (row) => row.SideEffectDetail || "-",
      sortable: true,
      wrap: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>หมายเหตุ</span>
        </HStack>
      ),
      selector: (row) => row.Remark || "-",
      sortable: true,
      wrap: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>สถานะ</span>
        </HStack>
      ),
      selector: (row) => (row.Status ? "ใช้งาน" : "ไม่ใช้งาน"),
      sortable: true,
      width: "100px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่สร้าง</span>
        </HStack>
      ),
      selector: (row) =>
        new Date(row.CreatedAt).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ผู้สร้าง</span>
        </HStack>
      ),
      selector: (row) => row.CreateBy || "-",
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่อัปเดต</span>
        </HStack>
      ),
      selector: (row) =>
        new Date(row.UpdatedAt).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ผู้อัปเดต</span>
        </HStack>
      ),
      selector: (row) => row.UpdatedBy || "-",
      sortable: true,
      width: "120px",
    },
  ];

  const exportToExcel = () => {
    const wsData = evaluateData.map((item) => ({
      รหัส: item.ID,
      วันที่ประเมิน: new Date(item.EvaluateDate).toLocaleString("th-TH", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      }),
      ชื่อผู้ประเมิน: item.EvaluateName,
      บริษัท: item.EvaluateCompany,
      จังหวัด: item.Province,
      รหัสวัคซีน: item.VaccineID,
      รหัสล็อต: item.LotID || "-",
      วันที่รับ: new Date(item.ReceiveDate).toLocaleString("th-TH", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      }),
      วันที่ใช้: new Date(item.UsedDate).toLocaleString("th-TH", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      }),
      ประเภทปศุสัตว์: item.LivestockTypeID,
      "จำนวนปศุสัตว์ (ตัว)": item.LivestockAmount || 0,
      "จำนวนแพ้วัคซีน (ตัว)": item.AllergicAmount || 0,
      รายละเอียดการแพ้: item.AllergicDetail || "-",
      "จำนวนผลข้างเคียง (ตัว)": item.SideEffectAmount || 0,
      รายละเอียดผลข้างเคียง: item.SideEffectDetail || "-",
      หมายเหตุ: item.Remark || "-",
      สถานะ: item.Status ? "ใช้งาน" : "ไม่ใช้งาน",
      วันที่สร้าง: new Date(item.CreatedAt).toLocaleString("th-TH", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      }),
      ผู้สร้าง: item.CreateBy || "-",
      วันที่อัปเดต: new Date(item.UpdatedAt).toLocaleString("th-TH", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      }),
      ผู้อัปเดต: item.UpdatedBy || "-",
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EvaluateReport");
    XLSX.writeFile(wb, "VaccineUsageEvaluateReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานสรุปผลการใช้วัคซีน", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "รหัส",
          "วันที่ประเมิน",
          "ชื่อผู้ประเมิน",
          "บริษัท",
          "จังหวัด",
          "รหัสวัคซีน",
          "รหัสล็อต",
          "วันที่รับ",
          "วันที่ใช้",
          "ประเภทปศุสัตว์",
          "จำนวนปศุสัตว์ (ตัว)",
          "จำนวนแพ้วัคซีน (ตัว)",
          "รายละเอียดการแพ้",
          "จำนวนผลข้างเคียง (ตัว)",
          "รายละเอียดผลข้างเคียง",
          "หมายเหตุ",
          "สถานะ",
          "วันที่สร้าง",
          "ผู้สร้าง",
          "วันที่อัปเดต",
          "ผู้อัปเดต",
        ],
      ],
      body: evaluateData.map((item) => [
        item.ID,
        new Date(item.EvaluateDate).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
        item.EvaluateName,
        item.EvaluateCompany,
        item.Province,
        item.VaccineID,
        item.LotID || "-",
        new Date(item.ReceiveDate).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
        new Date(item.UsedDate).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
        item.LivestockTypeID,
        item.LivestockAmount || 0,
        item.AllergicAmount || 0,
        item.AllergicDetail || "-",
        item.SideEffectAmount || 0,
        item.SideEffectDetail || "-",
        item.Remark || "-",
        item.Status ? "ใช้งาน" : "ไม่ใช้งาน",
        new Date(item.CreatedAt).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
        item.CreateBy || "-",
        new Date(item.UpdatedAt).toLocaleString("th-TH", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
        item.UpdatedBy || "-",
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 30 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 20 },
        7: { cellWidth: 30 },
        8: { cellWidth: 30 },
        9: { cellWidth: 20 },
        10: { cellWidth: 20 },
        11: { cellWidth: 20 },
        12: { cellWidth: 30 },
        13: { cellWidth: 20 },
        14: { cellWidth: 30 },
        15: { cellWidth: 30 },
        16: { cellWidth: 20 },
        17: { cellWidth: 30 },
        18: { cellWidth: 20 },
        19: { cellWidth: 30 },
        20: { cellWidth: 20 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("VaccineUsageEvaluateReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Vaccine Usage Evaluate Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Vaccine Usage Evaluate Report</h2>
          <table>
            <thead>
              <tr>
                <th>รหัส</th>
                <th>วันที่ประเมิน</th>
                <th>ชื่อผู้ประเมิน</th>
                <th>บริษัท</th>
                <th>จังหวัด</th>
                <th>รหัสวัคซีน</th>
                <th>รหัสล็อต</th>
                <th>วันที่รับ</th>
                <th>วันที่ใช้</th>
                <th>ประเภทปศุสัตว์</th>
                <th>จำนวนปศุสัตว์ (ตัว)</th>
                <th>จำนวนแพ้วัคซีน (ตัว)</th>
                <th>รายละเอียดการแพ้</th>
                <th>จำนวนผลข้างเคียง (ตัว)</th>
                <th>รายละเอียดผลข้างเคียง</th>
                <th>หมายเหตุ</th>
                <th>สถานะ</th>
                <th>วันที่สร้าง</th>
                <th>ผู้สร้าง</th>
                <th>วันที่อัปเดต</th>
                <th>ผู้อัปเดต</th>
              </tr>
            </thead>
            <tbody>
              ${evaluateData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.ID}</td>
                      <td>${new Date(item.EvaluateDate).toLocaleString(
                        "th-TH",
                        { day: "numeric", month: "numeric", year: "numeric" }
                      )}</td>
                      <td>${item.EvaluateName}</td>
                      <td>${item.EvaluateCompany}</td>
                      <td>${item.Province}</td>
                      <td>${item.VaccineID}</td>
                      <td>${item.LotID || "-"}</td>
                      <td>${new Date(item.ReceiveDate).toLocaleString("th-TH", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                      })}</td>
                      <td>${new Date(item.UsedDate).toLocaleString("th-TH", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                      })}</td>
                      <td>${item.LivestockTypeID}</td>
                      <td>${item.LivestockAmount || 0}</td>
                      <td>${item.AllergicAmount || 0}</td>
                      <td>${item.AllergicDetail || "-"}</td>
                      <td>${item.SideEffectAmount || 0}</td>
                      <td>${item.SideEffectDetail || "-"}</td>
                      <td>${item.Remark || "-"}</td>
                      <td>${item.Status ? "ใช้งาน" : "ไม่ใช้งาน"}</td>
                      <td>${new Date(item.CreatedAt).toLocaleString("th-TH", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                      })}</td>
                      <td>${item.CreateBy || "-"}</td>
                      <td>${new Date(item.UpdatedAt).toLocaleString("th-TH", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                      })}</td>
                      <td>${item.UpdatedBy || "-"}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    const livestockTypes = [
      ...new Set(evaluateData.map((item) => item.LivestockTypeID)),
    ].filter(Boolean);
    return livestockTypes.map((type) => {
      const relevantData = evaluateData.filter(
        (item) => item.LivestockTypeID === type
      );
      return {
        name: type,
        LivestockAmount: relevantData.reduce(
          (sum, item) => sum + (item.LivestockAmount || 0),
          0
        ),
        AllergicAmount: relevantData.reduce(
          (sum, item) => sum + (item.AllergicAmount || 0),
          0
        ),
        SideEffectAmount: relevantData.reduce(
          (sum, item) => sum + (item.SideEffectAmount || 0),
          0
        ),
      };
    });
  };

  const colors = [
    "#16A085", // Green for LivestockAmount
    "#E74C3C", // Red for AllergicAmount
    "#F1C40F", // Yellow for SideEffectAmount
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>ประเภทปศุสัตว์:</strong> {label}
          </p>
          {payload.map((entry) => (
            <p key={entry.name}>
              <strong>
                {entry.name === "LivestockAmount"
                  ? "จำนวนปศุสัตว์"
                  : entry.name === "AllergicAmount"
                  ? "จำนวนแพ้วัคซีน"
                  : "จำนวนผลข้างเคียง"}
                :
              </strong>{" "}
              {entry.value} ตัว
            </p>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานสรุปผลการใช้วัคซีน" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลสรุปผลการใช้วัคซีน"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่เริ่มต้น
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่สิ้นสุด
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                minDate={minDateForEndDate} // <--- ค่านี้จะเปลี่ยนไปตาม StartDate
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ชื่อผู้ประเมิน
              </FormLabel>
              <Input
                type="text"
                name="EvaluateName"
                value={filterData.EvaluateName}
                onChange={handleInputChange}
                placeholder="ระบุชื่อผู้ประเมิน (บางส่วน)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                จังหวัด
              </FormLabel>
              <Select
                name="Province"
                value={filterData.Province}
                onChange={handleInputChange}
                placeholder="เลือกจังหวัด (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {provinceOptions.map((province) => (
                  <option key={province.Province} value={province.Province}>
                    {province.Province}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                รหัสวัคซีน
              </FormLabel>
              <Input
                type="text"
                name="VaccineID"
                value={filterData.VaccineID}
                onChange={handleInputChange}
                placeholder="ระบุรหัสวัคซีน (บางส่วน)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทปศุสัตว์
              </FormLabel>
              <Select
                name="LivestockTypeID"
                value={filterData.LivestockTypeID}
                onChange={handleInputChange}
                placeholder="เลือกประเภทปศุสัตว์ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {livestockTypeOptions.map((type) => (
                  <option
                    key={type.LivestockTypeID}
                    value={type.LivestockTypeID}
                  >
                    {type.LivestockTypeID}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                สถานะ
              </FormLabel>
              <Select
                name="Status"
                value={filterData.Status}
                onChange={handleInputChange}
                placeholder="เลือกสถานะ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                <option value="1">ใช้งาน</option>
                <option value="0">ไม่ใช้งาน</option>
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    StartDate: "",
                    EndDate: "",
                    EvaluateName: "",
                    Province: "",
                    VaccineID: "",
                    LivestockTypeID: "",
                    Status: "",
                  })
                }
              />
              <Search onClick={fetchEvaluateReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {evaluateData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการการสรุปผลการใช้วัคซีน"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={evaluateData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {evaluateData.length > 0 && (
        <Box
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          p={5}
          mt={6}
          bg="white"
        >
          <StandardHeader
            title="กราฟสรุปผลการใช้วัคซีน"
            bgColor="iGreen.500"
            icon={faChartBar}
          />
          <ResponsiveContainer width="100%" height={500}>
            <BarChart
              data={chartData()}
              margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={150}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                label={{
                  value: "จำนวน (ตัว)",
                  angle: -90,
                  position: "insideLeft",
                  offset: -5,
                }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="right"
                wrapperStyle={{ paddingBottom: 10 }}
              />
              <Bar
                dataKey="LivestockAmount"
                name="จำนวนปศุสัตว์"
                fill={colors[0]}
                barSize={30}
              />
              <Bar
                dataKey="AllergicAmount"
                name="จำนวนแพ้วัคซีน"
                fill={colors[1]}
                barSize={30}
              />
              <Bar
                dataKey="SideEffectAmount"
                name="จำนวนผลข้างเคียง"
                fill={colors[2]}
                barSize={30}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default VaccineUsageEvaluateReport;
